import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { queueOp, queueAndFlush, flushQueue, getQueuedDeletedIds, getQueuedCreates } from "@/lib/syncQueue";

export function useSavedList(user, lastLocalUpdate, tutorialModeRef) {
  const [savedList, setSavedList] = useState([]);
  const [clearListConfirm, setClearListConfirm] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const pendingUpdates = useRef((() => {
    // Survive app close: pending item edits are persisted on the device
    try { return JSON.parse(localStorage.getItem('pending_item_updates') || '{}'); } catch { return {}; }
  })());
  const persistPendingItems = () => {
    try { localStorage.setItem('pending_item_updates', JSON.stringify(pendingUpdates.current)); } catch {}
  };
  const pendingReorder = useRef(null);
  const hadItems = useRef(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);

  // Restore a reorder that couldn't reach the server (offline move)
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('pending_reorder') || 'null');
      if (saved && Array.isArray(saved)) pendingReorder.current = saved;
    } catch {}
  }, []);

  const sendReorder = useCallback((payload) => {
    lastLocalUpdate.current = Date.now();
    pendingReorder.current = payload;
    try { localStorage.setItem('pending_reorder', JSON.stringify(payload)); } catch {}
    axios.put(`${API}/saved-list/reorder`, payload, { timeout: 15000 }).then(() => {
      // Keep the arrangement queued while offline-created items are still syncing,
      // so it re-applies once they land on the server (their order is assigned late)
      if (pendingReorder.current === payload && getQueuedCreates('saved_list').length === 0) {
        pendingReorder.current = null;
        localStorage.removeItem('pending_reorder');
      }
    }).catch(() => {});
  }, [lastLocalUpdate]);

  // Persist critical state to localStorage for offline resilience
  useEffect(() => {
    if (tutorialModeRef?.current) return;
    if (savedList.length > 0) {
      hadItems.current = true;
      const localData = savedList.map(i => ({
        id: i.id,
        stopwatch_elapsed: i.stopwatch_elapsed,
        stopwatch_started_at: i.stopwatch_started_at,
        stopwatch_updated_at: i.stopwatch_updated_at,
        lumber_custom_qty: i.lumber_custom_qty,
        lumber_custom_species: i.lumber_custom_species,
        lumber_checked: i.lumber_checked,
        lumber_extra_used: i.lumber_extra_used,
        lumber_adj_counts: i.lumber_adj_counts,
        custom_lumber: i.custom_lumber,
      }));
      localStorage.setItem('savedList_local', JSON.stringify(localData));
      // Full copy so the cut list renders instantly on next app open
      try { localStorage.setItem('savedList_cache', JSON.stringify({ email: user?.email || '', items: savedList })); } catch {}
    } else if (hadItems.current) {
      // List emptied THIS session (user deleted everything) — stale caches must not resurrect items
      try { localStorage.setItem('savedList_local', '[]'); } catch {}
      try { localStorage.setItem('savedList_cache', JSON.stringify({ email: user?.email || '', items: [] })); } catch {}
    }
  }, [savedList, tutorialModeRef, user]);

  // Flush pending updates periodically AND right after the sync queue lands offline creates
  useEffect(() => {
    const flushPending = async () => {
      if (pendingReorder.current && navigator.onLine) {
        const payload = pendingReorder.current;
        try {
          await axios.put(`${API}/saved-list/reorder`, payload, { timeout: 15000 });
          if (pendingReorder.current === payload && getQueuedCreates('saved_list').length === 0) {
            pendingReorder.current = null;
            localStorage.removeItem('pending_reorder');
          }
        } catch {}
      }
      const pending = { ...pendingUpdates.current };
      const ids = Object.keys(pending);
      if (ids.length === 0) { setPendingSyncCount(0); return; }
      setPendingSyncCount(ids.length);
      for (const id of ids) {
        const payload = pendingUpdates.current[id];
        if (!payload) continue;
        try {
          await axios.put(`${API}/saved-list/${id}`, payload, { timeout: 15000 });
          // Only clear if no newer fields were merged while this request was in flight
          if (pendingUpdates.current[id] === payload) { delete pendingUpdates.current[id]; persistPendingItems(); }
        } catch (err) {
          // 404 can mean "deleted" OR "offline-created and not on the server yet".
          // Only drop the pending update when the item truly isn't waiting to be created —
          // otherwise its local edits get lost and refetches flip the item back (flicker)
          if (err.response?.status === 404 && pendingUpdates.current[id] === payload) {
            let stillQueued = false;
            try {
              stillQueued = getQueuedCreates('saved_list').some(b =>
                (Array.isArray(b) ? b : [b]).some(i => i?.id === id));
            } catch {}
            if (!stillQueued) { delete pendingUpdates.current[id]; persistPendingItems(); }
          }
        }
      }
      setPendingSyncCount(Object.keys(pendingUpdates.current).length);
    };
    const interval = setInterval(flushPending, 5000);
    window.addEventListener('sync-queue-flushed', flushPending);
    return () => { clearInterval(interval); window.removeEventListener('sync-queue-flushed', flushPending); };
  }, []);

  const fetchSavedList = useCallback(async () => {
    if (tutorialModeRef?.current) return;
    try {
      const fetchStarted = Date.now();
      const { data: serverData } = await axios.get(`${API}/saved-list`);
      // A local change (move/check/edit) happened while this fetch was in flight —
      // applying the stale server snapshot would visually "glitch" the list backwards
      if (lastLocalUpdate.current > fetchStarted) return;
      // Merge offline-created items the server doesn't have yet, drop offline-deleted ones
      let data = serverData;
      const queuedCreateIds = [];
      try {
        const deletedIds = getQueuedDeletedIds("saved_list");
        if (deletedIds.length > 0) data = data.filter(d => !deletedIds.includes(d.id));
        getQueuedCreates("saved_list").forEach(body => {
          (Array.isArray(body) ? body : [body]).forEach(item => {
            if (item?.id && !data.find(d => d.id === item.id)) { data.push(item); queuedCreateIds.push(item.id); }
          });
        });
      } catch {}
      try {
        const localData = JSON.parse(localStorage.getItem('savedList_local') || '[]');
        if (localData.length > 0) {
          const localMap = {};
          localData.forEach(s => { localMap[s.id] = s; });
          const needsSync = [];
          data.forEach(item => {
            const local = localMap[item.id];
            if (!local) return;

            // Stopwatch merge — the most recent start/stop wins (stale caches must not resurrect timers)
            const localNewer = !item.stopwatch_updated_at || (local.stopwatch_updated_at && local.stopwatch_updated_at > item.stopwatch_updated_at);
            if (local.stopwatch_started_at && !item.stopwatch_started_at && localNewer) {
              item.stopwatch_started_at = local.stopwatch_started_at;
              item.stopwatch_elapsed = local.stopwatch_elapsed;
              needsSync.push({ id: item.id, stopwatch_started_at: local.stopwatch_started_at, stopwatch_elapsed: local.stopwatch_elapsed, stopwatch_updated_at: local.stopwatch_updated_at });
            } else if (!local.stopwatch_started_at && item.stopwatch_started_at && localNewer) {
              item.stopwatch_elapsed = local.stopwatch_elapsed;
              item.stopwatch_started_at = null;
              needsSync.push({ id: item.id, stopwatch_elapsed: local.stopwatch_elapsed, stopwatch_started_at: null, stopwatch_updated_at: local.stopwatch_updated_at });
            } else if (!local.stopwatch_started_at && !item.stopwatch_started_at && local.stopwatch_elapsed > (item.stopwatch_elapsed || 0) && localNewer) {
              item.stopwatch_elapsed = local.stopwatch_elapsed;
              needsSync.push({ id: item.id, stopwatch_elapsed: local.stopwatch_elapsed, stopwatch_started_at: null, stopwatch_updated_at: local.stopwatch_updated_at });
            }

            // Lumber data merge — local wins if server has empty/missing values
            const lumberFields = ['lumber_custom_qty', 'lumber_custom_species', 'lumber_checked', 'lumber_extra_used'];
            const syncFields = {};
            lumberFields.forEach(field => {
              const localVal = local[field];
              const serverVal = item[field];
              if (!localVal) return;
              // For arrays: local has data, server is empty or shorter
              if (Array.isArray(localVal) && localVal.length > 0) {
                const serverArr = Array.isArray(serverVal) ? serverVal : [];
                const localHasData = localVal.some(v => v !== "" && v !== false && v !== null && v !== undefined);
                const serverHasData = serverArr.some(v => v !== "" && v !== false && v !== null && v !== undefined);
                if (localHasData && !serverHasData) {
                  item[field] = localVal;
                  syncFields[field] = localVal;
                }
              }
              // For objects (lumber_extra_used)
              else if (typeof localVal === 'object' && !Array.isArray(localVal) && Object.keys(localVal).length > 0) {
                if (!serverVal || typeof serverVal !== 'object' || Object.keys(serverVal).length === 0) {
                  item[field] = localVal;
                  syncFields[field] = localVal;
                }
              }
            });
            if (Object.keys(syncFields).length > 0) {
              needsSync.push({ id: item.id, ...syncFields });
            }
          });

          for (const sync of needsSync) {
            axios.put(`${API}/saved-list/${sync.id}`, sync, { timeout: 15000 }).catch(() => {
              pendingUpdates.current[sync.id] = { ...pendingUpdates.current[sync.id], ...sync };
              persistPendingItems();
            });
          }
        }
      } catch {}
      // The newest locally-entered values ALWAYS win over older server values
      try {
        const pending = pendingUpdates.current;
        data.forEach(item => {
          if (pending[item.id]) Object.assign(item, pending[item.id]);
        });
      } catch {}
      // Not-yet-synced items: the live local version (position + edits) beats the raw
      // queued body — swapping bodies in made items visibly "pop" on flaky connections
      setSavedList(prev => data.map(item =>
        queuedCreateIds.includes(item.id) ? (prev.find(p => p.id === item.id) || item) : item
      ));
    } catch {}
  }, [tutorialModeRef, lastLocalUpdate]);

  useEffect(() => {
    if (user && user !== false) {
      // Hydrate instantly from the local copy (own account only), then refresh from server
      try {
        const cache = JSON.parse(localStorage.getItem('savedList_cache') || 'null');
        if (cache && cache.email === user.email && Array.isArray(cache.items) && cache.items.length > 0) {
          setSavedList(prev => (prev.length > 0 ? prev : cache.items));
        }
      } catch {}
      fetchSavedList();
    }
  }, [user, fetchSavedList]);

  const [cutLists, setCutLists] = useState([]);
  const fetchCutLists = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cut-lists`);
      setCutLists(data);
      try { localStorage.setItem('cutLists_cache', JSON.stringify({ email: user?.email || '', lists: data })); } catch {}
    } catch {}
  }, [user]);
  useEffect(() => {
    if (user && user !== false) {
      try {
        const cache = JSON.parse(localStorage.getItem('cutLists_cache') || 'null');
        if (cache && cache.email === user.email && Array.isArray(cache.lists) && cache.lists.length > 0) {
          setCutLists(prev => (prev.length > 0 ? prev : cache.lists));
        }
      } catch {}
      fetchCutLists();
    }
    const h = () => fetchCutLists();
    window.addEventListener('cut-lists-updated', h);
    return () => window.removeEventListener('cut-lists-updated', h);
  }, [user, fetchCutLists]);


  const removeFromList = useCallback(async (id) => {
    if (tutorialModeRef?.current) {
      setSavedList(prev => prev.filter(item => item.id !== id));
      return;
    }
    // Write-ahead: removal recorded on device BEFORE any network attempt — a hung
    // request or closed app can never resurrect the item
    lastLocalUpdate.current = Date.now();
    setSavedList(prev => prev.filter(item => item.id !== id));
    queueAndFlush({ method: "delete", path: `/saved-list/${id}`, entity: "saved_list", entityId: id });
    toast.success("Removed from list");
  }, [tutorialModeRef, lastLocalUpdate]);

  const toggleCheck = useCallback(async (id) => {
    if (tutorialModeRef?.current) {
      setSavedList(prev => prev.map(item => item.id === id ? { ...item, checked: !item.checked } : item));
      return;
    }
    // Optimistic toggle; falls back to the pending-update queue offline
    let newVal = null;
    setSavedList(prev => prev.map(item => {
      if (item.id !== id) return item;
      newVal = !item.checked;
      return { ...item, checked: newVal };
    }));
    lastLocalUpdate.current = Date.now();
    try { await axios.put(`${API}/saved-list/${id}/check`, null, { timeout: 15000 }); }
    catch {
      pendingUpdates.current[id] = { ...pendingUpdates.current[id], checked: newVal };
      persistPendingItems();
    }
  }, [tutorialModeRef, lastLocalUpdate]);

  const clearList = async () => {
    // clearListConfirm may hold a specific list id (string) or true (clear everything)
    const targetListId = typeof clearListConfirm === 'string' ? clearListConfirm : null;
    const defaultListId = cutLists[0]?.id;
    const inTarget = (i) => !targetListId || i.list_id === targetListId || (!i.list_id && targetListId === defaultListId);
    const keep = savedList.filter(i => i.saved_for_later || i.in_history || !inTarget(i));
    if (tutorialModeRef?.current) {
      setSavedList(keep); setClearListConfirm(false);
      return;
    }
    try {
      // Delete only active items in the target list, keep saved-for-later and history
      const activeIds = savedList.filter(i => !i.saved_for_later && !i.in_history && inTarget(i)).map(i => i.id);
      lastLocalUpdate.current = Date.now();
      setSavedList(keep); setClearListConfirm(false);
      // Write-ahead: every deletion recorded on device first, then synced
      activeIds.forEach(id => queueOp({ method: "delete", path: `/saved-list/${id}`, entity: "saved_list", entityId: id }));
      flushQueue();
      toast.success("Cut list cleared");
    }
    catch { toast.error("Failed to clear list"); }
  };

  // ONE update pattern for every editable cut-list field:
  // 1) newest value applied to UI instantly, 2) registered in pendingUpdates BEFORE the
  // network call (so no refetch can write an older server value back), 3) PUTs are
  // serialized per item so rapid edits always end on the latest value, 4) failures stay
  // queued and retry automatically (offline-safe).
  const putChains = useRef({});
  const applyItemUpdate = useCallback((id, fields) => {
    // Stamp stopwatch actions so the most recent start/stop wins across devices
    if ('stopwatch_started_at' in fields && !fields.stopwatch_updated_at) {
      fields = { ...fields, stopwatch_updated_at: new Date().toISOString() };
    }
    lastLocalUpdate.current = Date.now();
    setSavedList(prev => prev.map(i => i.id === id ? { ...i, ...fields } : i));
    if (tutorialModeRef?.current) return;
    pendingUpdates.current[id] = { ...pendingUpdates.current[id], ...fields };
    persistPendingItems();
    putChains.current[id] = (putChains.current[id] || Promise.resolve()).then(async () => {
      const payload = pendingUpdates.current[id];
      if (!payload) return;
      try {
        await axios.put(`${API}/saved-list/${id}`, payload, { timeout: 15000 });
        // Only clear if no NEWER fields were merged while this request was in flight
        if (pendingUpdates.current[id] === payload) { delete pendingUpdates.current[id]; persistPendingItems(); }
      } catch {
        // stays in pendingUpdates — retried by the flush interval / next sync
      }
    });
  }, [lastLocalUpdate, tutorialModeRef]);

  const updateItemCustomQty = useCallback((id, newTotal) => {
    const item = savedList.find(i => i.id === id);
    if (!item) return;
    const ratio = item.total > 0 ? newTotal / item.total : 1;
    const newLumber = item.lumber?.map(l => ({ ...l, quantity: l.quantity ? l.quantity * ratio : l.quantity })) || null;
    const newPalletQty = item.per_pallet > 0 ? newTotal / item.per_pallet : item.pallet_quantity;
    const newSkids = item.skids != null ? item.skids * ratio : null;
    applyItemUpdate(id, { total: newTotal, original_total: newTotal, pallet_quantity: newPalletQty, lumber: newLumber, skids: newSkids });
    if (!tutorialModeRef?.current) toast.success("Quantity updated!");
  }, [savedList, applyItemUpdate, tutorialModeRef]);

  const updateItemStopwatch = useCallback((id, fields) => {
    applyItemUpdate(id, fields);
  }, [applyItemUpdate]);

  const reorderTimer = useRef(null);
  const moveItem = useCallback(async (itemId, direction) => {
    setSavedList(prev => {
      const idx = prev.findIndex(i => i.id === itemId);
      if (idx === -1) return prev;
      const moved0 = prev[idx];
      // Only swap with items visible in the same section: same saved-for-later state,
      // not in history, and belonging to the same cut list (null = default/first list)
      const defaultListId = cutLists[0]?.id || null;
      const norm = (lid) => lid || defaultListId;
      const sameGroup = (i) => !!i.saved_for_later === !!moved0.saved_for_later && !i.in_history && norm(i.list_id) === norm(moved0.list_id);
      let targetIdx = -1;
      if (direction === -1) {
        for (let i = idx - 1; i >= 0; i--) {
          if (sameGroup(prev[i])) { targetIdx = i; break; }
        }
      } else {
        for (let i = idx + 1; i < prev.length; i++) {
          if (sameGroup(prev[i])) { targetIdx = i; break; }
        }
      }
      if (targetIdx === -1) return prev;
      const updated = [...prev];
      const [moved] = updated.splice(idx, 1);
      updated.splice(targetIdx, 0, moved);
      if (!tutorialModeRef?.current) {
        lastLocalUpdate.current = Date.now();
        // Debounce: rapid clicks send ONE final order snapshot, avoiding out-of-order saves
        const payload = updated.map((item, i) => ({ id: item.id, order: i }));
        if (reorderTimer.current) clearTimeout(reorderTimer.current);
        reorderTimer.current = setTimeout(() => sendReorder(payload), 400);
      }
      return updated;
    });
  }, [lastLocalUpdate, tutorialModeRef, cutLists, sendReorder]);

  const sortByThickness = useCallback(async (mode = 'thickness-asc') => {
    const labels = {
      'thickness-asc': 'Sorted: thinnest to thickest',
      'thickness-desc': 'Sorted: thickest to thinnest',
      'width-asc': 'Sorted: narrowest to widest',
      'width-desc': 'Sorted: widest to narrowest',
    };
    setSavedList(prev => {
      const sorted = [...prev].sort((a, b) => {
        if (mode === 'thickness-asc') {
          // thinnest first, then narrowest, then shortest
          const d1 = (a.thickness || 0) - (b.thickness || 0);
          if (d1 !== 0) return d1;
          const d2 = (a.height || 0) - (b.height || 0);
          if (d2 !== 0) return d2;
          return (a.length || 0) - (b.length || 0);
        }
        if (mode === 'thickness-desc') {
          // thickest first, then widest, then longest
          const d1 = (b.thickness || 0) - (a.thickness || 0);
          if (d1 !== 0) return d1;
          const d2 = (b.height || 0) - (a.height || 0);
          if (d2 !== 0) return d2;
          return (b.length || 0) - (a.length || 0);
        }
        if (mode === 'width-asc') {
          // narrowest first, then thinnest, then shortest
          const d1 = (a.height || 0) - (b.height || 0);
          if (d1 !== 0) return d1;
          const d2 = (a.thickness || 0) - (b.thickness || 0);
          if (d2 !== 0) return d2;
          return (a.length || 0) - (b.length || 0);
        }
        if (mode === 'width-desc') {
          // widest first, then thickest, then longest
          const d1 = (b.height || 0) - (a.height || 0);
          if (d1 !== 0) return d1;
          const d2 = (b.thickness || 0) - (a.thickness || 0);
          if (d2 !== 0) return d2;
          return (b.length || 0) - (a.length || 0);
        }
        return 0;
      });
      if (!tutorialModeRef?.current) {
        lastLocalUpdate.current = Date.now();
        sendReorder(sorted.map((item, i) => ({ id: item.id, order: i })));
        toast.success(labels[mode] || "Sorted");
      }
      return sorted;
    });
  }, [lastLocalUpdate, tutorialModeRef, sendReorder]);

  const addToList = async (calculationResults, listId = null) => {
    const resultsArr = (Array.isArray(calculationResults) ? calculationResults : [calculationResults]).filter(Boolean);
    if (resultsArr.length === 0) return;
    // ALL components from ALL results in ONE instant update + ONE queued save
    const newItems = resultsArr.flatMap(calculationResult => calculationResult.components
      .filter((comp) => !(comp.name || "").toLowerCase().includes("nail"))
      .map(comp => ({
        id: crypto.randomUUID(), timestamp: new Date().toISOString(),
        pallet_type_name: calculationResult.pallet_type_name, pallet_quantity: calculationResult.pallet_quantity,
        name: comp.name, total: comp.total, original_total: comp.total, per_pallet: comp.per_pallet,
        length: comp.length, height: comp.height, thickness: comp.thickness,
        notched: comp.notched, notch_length: comp.notch_length, minutes: comp.pcs_per_min,
        pcs_per_min: comp.pcs_per_min,
        board_footage: comp.board_footage, cost: comp.cost, lumber: comp.lumber, skids: comp.skids, boards_per_skid: comp.boards_per_skid, extras: comp.extras, checked: false,
        stopwatch_elapsed: 0, stopwatch_started_at: null,
        list_id: listId,
      })));
    if (newItems.length === 0) return;
    if (tutorialModeRef?.current) {
      setSavedList(prev => [...prev, ...newItems]);
      toast.success("Added to list!");
      return;
    }
    // Optimistic + write-ahead: show INSTANTLY, saved on device first, synced right after
    lastLocalUpdate.current = Date.now();
    setSavedList(prev => [...prev, ...newItems]);
    toast.success("Added to list!");
    queueAndFlush({ method: "post", path: "/saved-list", body: newItems, entity: "saved_list", entityId: newItems[0]?.id });
  };

  const addComponentToList = async (calculationResult, compIdx, listId = null) => {
    if (!calculationResult) return;
    const comp = calculationResult.components[compIdx];
    if (!comp) return;
    if ((comp.name || "").toLowerCase().includes("nail")) { toast.info("Nails components do not get added to the cut list"); return; }
    const newItem = {
      id: crypto.randomUUID(), timestamp: new Date().toISOString(),
      pallet_type_name: calculationResult.pallet_type_name, pallet_quantity: calculationResult.pallet_quantity,
      name: comp.name, total: comp.total, original_total: comp.total, per_pallet: comp.per_pallet,
      length: comp.length, height: comp.height, thickness: comp.thickness,
      notched: comp.notched, notch_length: comp.notch_length, minutes: comp.pcs_per_min,
      pcs_per_min: comp.pcs_per_min,
      board_footage: comp.board_footage, cost: comp.cost, lumber: comp.lumber, skids: comp.skids, boards_per_skid: comp.boards_per_skid, extras: comp.extras, checked: false,
      list_id: listId,
      stopwatch_elapsed: 0, stopwatch_started_at: null,
    };
    if (tutorialModeRef?.current) {
      setSavedList(prev => [...prev, newItem]);
      toast.success(`${comp.name} added to list!`);
      return;
    }
    // Optimistic + write-ahead: show INSTANTLY, saved on device first, synced right after
    lastLocalUpdate.current = Date.now();
    setSavedList(prev => [...prev, newItem]);
    toast.success(`${comp.name} added to list!`);
    queueAndFlush({ method: "post", path: "/saved-list", body: [newItem], entity: "saved_list", entityId: newItem.id });
  };

  const moveToHistory = useCallback(async (id) => {
    const item = savedList.find(i => i.id === id);
    if (!item) return;
    let elapsed = item.stopwatch_elapsed || 0;
    if (item.stopwatch_started_at) {
      elapsed += Math.floor((Date.now() - new Date(item.stopwatch_started_at).getTime()) / 1000);
    }
    const fields = {
      in_history: true,
      history_moved_at: new Date().toISOString(),
      hidden_from_monthly: false,
      stopwatch_elapsed: elapsed,
      stopwatch_started_at: null,
    };
    setSavedList(prev => prev.map(i => i.id === id ? { ...i, ...fields } : i));
    if (tutorialModeRef?.current) { toast.success("Moved to history"); return; }
    applyItemUpdate(id, fields);
    toast.success("Moved to history");
  }, [savedList, applyItemUpdate, tutorialModeRef]);

  const toggleSavedForLater = useCallback((id) => {
    const item = savedList.find(i => i.id === id);
    if (!item) return;
    const newVal = !item.saved_for_later;
    if (tutorialModeRef?.current) {
      setSavedList(prev => prev.map(i => i.id === id ? { ...i, saved_for_later: newVal } : i));
      return;
    }
    applyItemUpdate(id, { saved_for_later: newVal });
  }, [savedList, applyItemUpdate, tutorialModeRef]);

  return {
    savedList, setSavedList, clearListConfirm, setClearListConfirm, dragIdx, setDragIdx,
    fetchSavedList, removeFromList, toggleCheck, clearList,
    updateItemCustomQty, updateItemStopwatch, moveItem, sortByThickness,
    addToList, addComponentToList, toggleSavedForLater, moveToHistory,
    cutLists, fetchCutLists, pendingSyncCount,
  };
}
